import React, { useState } from 'react';

interface VoiceControlsProps {
  isListening: boolean;
  onStartListening: () => void;
  onStopListening: () => void;
  disabled?: boolean;
  language: string;
}

const VoiceControls: React.FC<VoiceControlsProps> = ({
  isListening,
  onStartListening,
  onStopListening,
  disabled = false,
  language
}) => {
  const [permissionDenied, setPermissionDenied] = useState(false);

  const handleMicrophoneClick = async () => {
    if (disabled) return;
    
    if (isListening) {
      onStopListening();
    } else {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setPermissionDenied(false);
        onStartListening();
      } catch (error) {
        console.error('Microphone permission denied:', error);
        setPermissionDenied(true);
        setTimeout(() => setPermissionDenied(false), 3000);
      }
    }
  };

  const getMicrophoneTooltip = () => {
    if (permissionDenied) {
      return language === 'zh-TW' ? '請允許麥克風權限' : 
             language === 'zh-CN' ? '请允许麦克风权限' : 
             'Please allow microphone access';
    }
    if (isListening) {
      return language === 'zh-TW' ? '點擊停止錄音' : 
             language === 'zh-CN' ? '点击停止录音' : 
             'Click to stop recording';
    }
    return language === 'zh-TW' ? '點擊開始語音輸入' : 
           language === 'zh-CN' ? '点击开始语音输入' : 
           'Click to start voice input';
  };


  return (
    <div className="voice-controls" style={{
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      marginRight: '8px'
    }}>
      <button
        type="button"
        className={`voice-control-btn microphone-btn ${isListening ? 'listening' : ''} ${permissionDenied ? 'error' : ''}`}
        onClick={handleMicrophoneClick}
        disabled={disabled}
        title={getMicrophoneTooltip()}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: isListening ? '2px solid #ff4444' : '1px solid #ddd',
          backgroundColor: isListening ? '#ffeeee' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          position: 'relative',
          animation: isListening ? 'pulse 1.5s infinite' : 'none'
        }}
      >
        <i 
          className={`fas ${isListening ? 'fa-microphone-slash' : 'fa-microphone'}`}
          style={{
            color: permissionDenied ? '#ff4444' : (isListening ? '#ff4444' : '#666'),
            fontSize: '16px'
          }}
        />
        {isListening && (
          <span className="recording-indicator" style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            width: '12px',
            height: '12px',
            backgroundColor: '#ff4444',
            borderRadius: '50%',
            animation: 'blink 1s infinite'
          }} />
        )}
      </button>

      <style jsx>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(255, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(255, 68, 68, 0);
          }
        }

        @keyframes blink {
          0%, 50%, 100% {
            opacity: 1;
          }
          25%, 75% {
            opacity: 0.5;
          }
        }

        .voice-control-btn:hover:not(:disabled) {
          transform: scale(1.1);
        }

        .voice-control-btn:active:not(:disabled) {
          transform: scale(0.95);
        }

        .voice-control-btn:disabled {
          opacity: 0.5;
        }
      `}</style>
    </div>
  );
};

export default VoiceControls;